import { TextField } from "@material-ui/core";
import { connect, getIn } from "formik";
import React from "react";
import {
  formatPhoneNumberIntl,
  parsePhoneNumber,
} from "react-phone-number-input";
import styled from "styled-components";
import cuideoTheme from "../../containers/themes/defaultTheme";

const StyledField = styled("div")<{
  fullWidth: boolean;
  margin: string;
  size: string;
}>`
  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  padding-top: 0;
  padding-bottom: 0;
  ${({ margin }) => {
    if (margin === "normal") {
      return `
      margin: 1rem 0;
      `;
    }
    if (margin === "nomargin") {
      return ``;
    }
  }}

  &:last-child {
    border-bottom: none;
  }
`;

const StyledTextField = styled(TextField)<{
  size: string;
  error: boolean;
  disabled: boolean;
}>`
  .MuiOutlinedInput-root {
    height: auto;
    font-weight: 600;
    background-color: ${({ error, disabled }) =>
      error
        ? "#fef5f7"
        : disabled
        ? "#ededed"
        : cuideoTheme.palette.common.white};
    border-radius: ${cuideoTheme.shape.borderRadius}px;
    color: ${cuideoTheme.palette.primary.main};
  }

  ${({ size }) => {
    if (size === "medium") {
      return `
      .MuiOutlinedInput-root {
        font-size: 1.125rem;
      }
      .MuiOutlinedInput-input {
        padding-top: 0.875rem;
        padding-bottom: 0.875rem;
      }
      .MuiOutlinedInput-multiline {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }
      .MuiOutlinedInput-inputMultiline {
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
      }

      .MuiFormHelperText-root {
        font-size: 0.8rem;
        margin: 0.25rem;
      }
      @media all and (min-width: ${cuideoTheme.breakpoints.values.md}px) {
        .MuiOutlinedInput-root {
          font-size: 1.125rem;
        }
      }
          `;
    }
    if (size === "small") {
      return `
      .MuiOutlinedInput-root {
        font-size: 1rem;
      }
      .MuiOutlinedInput-input {
        padding-top: 0.6rem;
        padding-bottom: 0.6rem;
      }
      .MuiOutlinedInput-multiline {
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }
      .MuiOutlinedInput-inputMultiline {
        padding-top: 0.25rem;
        padding-bottom: 0.25rem;
      }

      .MuiFormHelperText-root {
        font-size: 0.8rem;
        margin: 0.25rem;
      }
      @media all and (min-width: ${cuideoTheme.breakpoints.values.md}px) {
        .MuiOutlinedInput-root {
          font-size: 1rem;
        }
      }
          `;
    }
  }}
`;

export interface FormikTextFieldProps {
  name: string;
  type?: "text" | "number" | "password" | "postalCode" | "tel";
  size?: "medium" | "small";
  margin?: "nomargin" | "normal";
  variant?: "standard" | "outlined" | "filled";
  label?: any;
  helperText?: any;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: string;
  maxRows?: string;
  uppercase?: boolean;
  telformat?: boolean;
  placeholder?: string;
  required?: boolean;
  endAdornment?: any;
  forceComponentUpdate?: boolean;
  onChange?: Function;
  onBlur?: Function;
  onKeyDown?: Function;
  autoComplete?: any;
  role?: string;
  validate?: Function;
  disabled?: boolean;
  autoFocus?: boolean;
}

interface State {}

class FormikOutlinedTextField extends React.Component<any, State> {
  shouldComponentUpdate(nextProps: any, nextState: any) {
    if (nextProps.forceComponentUpdate) {
      return true;
    }
    const next = nextProps.formik;
    const cur = this.props.formik;
    const name = this.props.name;
    if (
      getIn(next.values, name) !== getIn(cur.values, name) ||
      getIn(next.errors, name) !== getIn(cur.errors, name) ||
      getIn(next.touched, name) !== getIn(cur.touched, name)
    ) {
      return true;
    }
    return false;
  }

  componentDidMount() {
    this.props.formik.registerField(this.props.name, {
      validate: this.props.validate,
    });
  }

  componentDidUpdate(prevProps: any) {
    if (this.props.name !== prevProps.name) {
      this.props.formik.unregisterField(prevProps.name);
      this.props.formik.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }

    if (this.props.validate !== prevProps.validate) {
      this.props.formik.registerField(this.props.name, {
        validate: this.props.validate,
      });
    }
  }

  componentWillUnmount() {
    this.props.formik.unregisterField(this.props.name);
  }

  toUpperCase(value: string) {
    return value.toUpperCase();
  }

  toPhoneFormat(value: string) {
    let newPhone = value;
    if (newPhone.length > 0 && newPhone.charAt(0) !== "+") {
      newPhone = "+34" + newPhone;
    }

    const parsedNumber = parsePhoneNumber(newPhone);
    if (parsedNumber) {
      return formatPhoneNumberIntl(newPhone);
    }
    return value;
  }

  handleChange(e: any) {
    if (this.props.uppercase) {
      e.target.value = this.toUpperCase(e.target.value);
    }
    if (this.props.telformat) {
      e.target.value = this.toPhoneFormat(e.target.value);
    }
    if (this.props.type && this.props.type === "postalCode") {
      e.target.value = e.target.value.replace(/[^0-9]/g, "").substring(0, 5);
    }

    if (this.props.onChange) {
      this.props.onChange(e);
    }

    this.props.formik.handleChange(e);
  }

  handleBlur(e: any) {
    if (this.props.uppercase) {
      e.target.value = this.toUpperCase(e.target.value);
    }
    if (this.props.telformat) {
      e.target.value = this.toPhoneFormat(e.target.value);
    }

    if (this.props.onBlur) {
      this.props.onBlur(e);
    }

    this.props.formik.handleBlur(e);
  }

  render() {
    const {
      name,
      variant,
      label,
      helperText,
      fullWidth,
      multiline,
      rows,
      maxRows,
      margin,
      size,
      placeholder,
      endAdornment,
      type,
      onKeyDown,
      autoComplete,
      role,
      disabled,
      autoFocus,
    } = this.props;
    const formik = this.props.formik;

    const field = {
      value: getIn(formik.values, name),
      name,
      onChange: formik.handleChange,
      onBlur: formik.handleBlur,
    };
    // if (uppercase) {
    //   field.onChange = this.handleUpperCaseChange.bind(this);
    // }
    // if (telformat) {
    //   field.onChange = this.handlePhoneChange.bind(this);
    // }
    field.onChange = this.handleChange.bind(this);
    field.onBlur = this.handleBlur.bind(this);

    const error = getIn(formik.errors, name);
    const touched = !!getIn(formik.touched, name);

    // const meta = {
    //   value: getIn(formik.values, name),
    //   error,
    //   touched,
    //   initialValue: getIn(formik.initialValues, name),
    //   initialTouched: !!getIn(formik.initialTouched, name),
    //   initialError: getIn(formik.initialErrors, name)
    // };

    const parsedType = type === "postalCode" ? "number" : type;

    return (
      <StyledField fullWidth margin={margin || "normal"} size={size || "small"}>
        <StyledTextField
          {...field}
          fullWidth={fullWidth}
          variant={variant || "outlined"}
          multiline={multiline}
          rows={rows}
          label={label}
          type={parsedType}
          placeholder={placeholder}
          maxRows={maxRows}
          error={touched && !!error}
          helperText={touched && error ? error : helperText}
          size={size || "small"}
          InputProps={{ endAdornment }}
          onKeyDown={onKeyDown}
          autoComplete={autoComplete}
          role={role}
          disabled={disabled}
          autoFocus={autoFocus}
        />
      </StyledField>
    );
  }
}

export default connect<FormikTextFieldProps, State>(
  FormikOutlinedTextField as any
);
